import { getCurrentWeekId } from './weekId';

/**
 * Utility function to determine if a user is a "Weekly Active Trader".
 *
 * Preferred path: compares the user's stored `lastActiveWeek` (week ID
 * written by `captureUserIP` on every authenticated page load) against the
 * current week ID. This is what publicProfiles carries — the badge stays
 * accurate after the PII isolation flip even though publicProfiles never
 * exposes the precise `lastActiveAt` timestamp.
 *
 * Fallback path: when only `lastActiveAt` is available (e.g. legacy
 * sessionStorage payloads cached before the publicProfiles switch, or
 * authenticated self-reads from /users that haven't been refreshed yet),
 * we fall back to the original 10.5-day rolling window so existing
 * sessions don't lose the badge mid-flight.
 *
 * @param {Object} user - User object from Firestore, sessionStorage, or publicProfiles
 * @returns {boolean} - True if user is considered active this week, false otherwise
 */

export const isWeeklyActiveTrader = (user) => {
  if (!user) return false;

  // Preferred: weekly bucket comparison. This is what publicProfiles exposes.
  if (user.lastActiveWeek) {
    return user.lastActiveWeek === getCurrentWeekId();
  }

  // Fallback: legacy 10.5-day rolling window using raw timestamp.
  // Only reachable when reading from /users (self-reads) or stale sessionStorage.
  if (!user.lastActiveAt) {
    return false;
  }

  let lastActiveDate;

  // Handle Firestore Timestamp (has .toDate method)
  if (user.lastActiveAt?.toDate) {
    lastActiveDate = user.lastActiveAt.toDate();
  }
  // Handle ISO date string (from sessionStorage)
  else if (typeof user.lastActiveAt === 'string') {
    lastActiveDate = new Date(user.lastActiveAt);
  }
  // Handle timestamp number (milliseconds)
  else if (typeof user.lastActiveAt === 'number') {
    lastActiveDate = new Date(user.lastActiveAt);
  }
  // Handle plain Date object
  else if (user.lastActiveAt instanceof Date) {
    lastActiveDate = user.lastActiveAt;
  }
  // Handle serialized Firestore Timestamp object {seconds, nanoseconds} (Firebase SDK v9+/v10)
  else if (user.lastActiveAt.seconds !== undefined) {
    lastActiveDate = new Date(user.lastActiveAt.seconds * 1000);
  }
  // Handle legacy serialized Firestore Timestamp object {_seconds, _nanoseconds}
  else if (user.lastActiveAt._seconds !== undefined) {
    lastActiveDate = new Date(user.lastActiveAt._seconds * 1000);
  }
  // Unknown format
  else {
    return false;
  }

  // Validate the date is valid
  if (isNaN(lastActiveDate.getTime())) {
    return false;
  }

  const now = new Date();
  const diffMs = now - lastActiveDate;
  const oneAndHalfWeeks = 10.5 * 24 * 60 * 60 * 1000;

  return diffMs <= oneAndHalfWeeks;
};

export default isWeeklyActiveTrader;
